import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import * as Icons from 'lucide-react';
import { habitApiService, UpdateHabitRequest, ColorOption } from '../../services/habitApi';
import { aiSuggestionApiService, AiLimitExceededError } from '../../services/aiSuggestionApi';
import { useHabitStore } from '../../store/habitStore';
import { useSubscriptionStore } from '../../store/subscriptionStore';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { Habit, HabitFrequency } from '../../types';
import toast from 'react-hot-toast';

interface EditHabitFormProps {
  habit: Habit;
  onSuccess?: () => void;
  onCancel?: () => void;
}

// Popular icons for habits (same as CreateHabitForm)
const POPULAR_ICONS = [
  { name: 'Activity', icon: Icons.Activity },
  { name: 'Book', icon: Icons.Book },
  { name: 'Coffee', icon: Icons.Coffee },
  { name: 'Dumbbell', icon: Icons.Dumbbell },
  { name: 'Heart', icon: Icons.Heart },
  { name: 'Home', icon: Icons.Home },
  { name: 'Moon', icon: Icons.Moon },
  { name: 'Music', icon: Icons.Music },
  { name: 'Run', icon: Icons.Zap },
  { name: 'Smile', icon: Icons.Smile },
  { name: 'Star', icon: Icons.Star },
  { name: 'Sun', icon: Icons.Sun },
  { name: 'Target', icon: Icons.Target },
  { name: 'Tree', icon: Icons.TreePine },
  { name: 'Droplet', icon: Icons.Droplet },
  { name: 'Flame', icon: Icons.Flame },
  { name: 'Apple', icon: Icons.Apple },
  { name: 'Bike', icon: Icons.Bike },
  { name: 'Camera', icon: Icons.Camera },
  { name: 'Clock', icon: Icons.Clock },
  { name: 'Eye', icon: Icons.Eye },
  { name: 'Gift', icon: Icons.Gift },
  { name: 'Headphones', icon: Icons.Headphones },
  { name: 'Leaf', icon: Icons.Leaf },
  { name: 'Lightbulb', icon: Icons.Lightbulb },
  { name: 'Map', icon: Icons.Map },
  { name: 'Palette', icon: Icons.Palette },
  { name: 'Phone', icon: Icons.Phone },
  { name: 'Shield', icon: Icons.Shield },
  { name: 'Umbrella', icon: Icons.Umbrella },
  { name: 'Circle', icon: Icons.Circle },
  { name: 'Square', icon: Icons.Square },
  { name: 'Triangle', icon: Icons.Triangle },
  { name: 'Diamond', icon: Icons.Diamond },
  { name: 'Hexagon', icon: Icons.Hexagon },
  { name: 'Octagon', icon: Icons.Octagon },
];

export default function EditHabitForm({ habit, onSuccess, onCancel }: EditHabitFormProps) {
  const [suggestedColors, setSuggestedColors] = useState<ColorOption[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGeneratingSuggestion, setIsGeneratingSuggestion] = useState(false);

  // Get AI usage info
  const { aiSuggestionsUsedToday, maxAiSuggestionsPerDay, canUseAiToday } = useFeatureAccess();

  // Get existing habit names for duplicate validation (excluding current habit)
  const { getActiveHabits } = useHabitStore();
  const existingHabits = getActiveHabits();
  const existingHabitNames = existingHabits
    .filter(h => h.id !== habit.id) // Exclude current habit
    .map(h => h.name.toLowerCase());

  // Check if habit has any completions (which would prevent frequency changes)
  const hasCompletions = habit.currentStreak > 0 || habit.longestStreak > 0;

  // Form validation schema
  const editHabitSchema = z.object({
    name: z.string()
      .min(1, 'Habit name is required')
      .max(100, 'Name must be 100 characters or less')
      .refine(
        (name) => !existingHabitNames.includes(name.toLowerCase()),
        'A habit with this name already exists'
      ),
    description: z.string().max(500, 'Description must be 500 characters or less').optional(),
    iconName: z.string().min(1, 'Please select an icon'),
    colorCode: z.string().regex(/^#[0-9A-F]{6}$/i, 'Please select a color'),
    targetStreakLength: z.number().min(1, 'Target streak must be at least 1 day').max(365, 'Target streak must be 365 days or less'),
    isActive: z.boolean(),
  });

  type EditHabitFormData = z.infer<typeof editHabitSchema>;

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<EditHabitFormData>({
    resolver: zodResolver(editHabitSchema),
    defaultValues: {
      name: habit.name,
      description: habit.description || '',
      iconName: habit.iconName,
      colorCode: habit.colorCode,
      targetStreakLength: habit.targetStreakLength,
      isActive: habit.isActive,
    },
  });

  const watchedColorCode = watch('colorCode');
  const watchedIconName = watch('iconName');
  const watchedIsActive = watch('isActive');
  const watchedName = watch('name');
  const watchedDescription = watch('description');

  // Load colors on component mount
  useEffect(() => {
    const loadColors = async () => {
      try {
        const colors = await habitApiService.getSuggestedColors();
        setSuggestedColors(colors);
      } catch (error) {
        console.error('Failed to load colors:', error);
        toast.error('Failed to load color options');
      }
    };

    loadColors();
  }, []);

  const handleAiSuggestion = async () => {
    if (!watchedName.trim()) {
      toast.error('Please enter a habit name first');
      return;
    }

    if (!canUseAiToday) {
      toast.error(`Daily AI limit reached (${aiSuggestionsUsedToday}/${maxAiSuggestionsPerDay}). Upgrade for more.`);
      return;
    }

    setIsGeneratingSuggestion(true);

    try {
      const suggestion = await aiSuggestionApiService.getSuggestion({
        title: watchedName,
        context: watchedDescription || undefined,
      });

      // Apply the AI suggestion to the form (excluding frequency since it can't be changed)
      setValue('name', suggestion.name);
      setValue('description', suggestion.description);
      setValue('iconName', suggestion.iconName.toLowerCase());
      setValue('colorCode', suggestion.colorCode);
      setValue('targetStreakLength', suggestion.targetStreakLength);

      toast.success(`AI suggestion applied! ${suggestion.reasoning}`);

      // Refresh feature access to update AI usage counter
      useSubscriptionStore.getState().loadFeatureAccess();
    } catch (error) {
      console.error('Failed to get AI suggestion:', error);

      if (error instanceof AiLimitExceededError) {
        toast.error(`Daily AI limit reached (${error.usedToday}/${error.dailyLimit}). Upgrade for more.`);
      } else {
        toast.error(error instanceof Error ? error.message : 'Failed to generate AI suggestion');
      }
    } finally {
      setIsGeneratingSuggestion(false);
    }
  };

  const onSubmit = async (data: EditHabitFormData) => {
    setIsSubmitting(true);
    
    try {
      const request: UpdateHabitRequest = {
        name: data.name,
        description: data.description || '',
        iconName: data.iconName,
        colorCode: data.colorCode,
        targetStreakLength: data.targetStreakLength,
        isActive: data.isActive,
      };

      console.log('Updating habit:', request);
      await habitApiService.updateHabit(habit.id, request);
      toast.success('Habit updated successfully!');
      onSuccess?.();
    } catch (error) {
      console.error('Failed to update habit:', error);
      
      // Handle specific error messages from the backend
      if (error instanceof Error) {
        if (error.message.includes('already exists')) {
          toast.error(error.message);
        } else {
          toast.error('Failed to update habit');
        }
      } else {
        toast.error('Failed to update habit');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to get frequency display text
  const getFrequencyDisplayText = () => {
    switch (habit.frequency) {
      case HabitFrequency.Daily:
        return 'Daily';
      case HabitFrequency.WeeklySpecificDays:
        return 'Specific days of the week';
      case HabitFrequency.WeeklyCount:
        return 'X times per week';
      case HabitFrequency.IntervalBased:
        return 'Every X days';
      default:
        return 'Daily';
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Edit Habit</h2>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
          >
            <Icons.X size={20} />
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Habit Name */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Habit Name
            </label>
            <div className="flex items-center gap-2">
              {/* AI Usage Counter */}
              {maxAiSuggestionsPerDay > 0 && (
                <span className={`text-xs ${canUseAiToday ? 'text-gray-500 dark:text-gray-400' : 'text-red-500 dark:text-red-400'}`}>
                  {aiSuggestionsUsedToday}/{maxAiSuggestionsPerDay} remaining
                </span>
              )}
              <button
                type="button"
                onClick={handleAiSuggestion}
                disabled={isGeneratingSuggestion || !watchedName.trim() || !canUseAiToday}
                className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 dark:text-blue-400 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title={!canUseAiToday ? 'Daily AI limit reached. Upgrade for more.' : ''}
              >
                {isGeneratingSuggestion ? (
                  <>
                    <Icons.Loader2 size={14} className="animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Icons.Sparkles size={14} />
                    AI Suggest
                  </>
                )}
              </button>
            </div>
          </div>
          <input
            {...register('name')}
            placeholder="Enter habit name"
            className="input"
          />
          {errors.name && (
            <p className="text-red-500 dark:text-red-400 text-sm mt-1">{errors.name.message}</p>
          )}
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Description (Optional)
          </label>
          <textarea
            {...register('description')}
            placeholder="Add notes or description"
            rows={3}
            className="w-full px-4 py-3 rounded-xl bg-[#f1f2f4] dark:bg-gray-700 border-none text-[#121416] dark:text-gray-100 placeholder-[#6a7681] dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 resize-none"
          />
          {errors.description && (
            <p className="text-red-500 dark:text-red-400 text-sm mt-1">{errors.description.message}</p>
          )}
        </div>

        {/* Icon Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Icon
          </label>
          <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-10 gap-3 max-h-40 overflow-y-auto">
            {POPULAR_ICONS.map((iconData) => {
              const IconComponent = iconData.icon;
              const isSelected = watchedIconName === iconData.name.toLowerCase();
              
              return (
                <label
                  key={iconData.name}
                  className={`w-10 h-10 rounded-lg border-2 cursor-pointer transition-all flex items-center justify-center ${
                    isSelected
                      ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/30'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                  title={iconData.name}
                >
                  <input
                    type="radio"
                    className="sr-only"
                    {...register('iconName')}
                    value={iconData.name.toLowerCase()}
                  />
                  <IconComponent 
                    size={20} 
                    className={isSelected ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'} 
                  />
                </label>
              );
            })}
          </div>
          {errors.iconName && (
            <p className="text-red-500 dark:text-red-400 text-sm mt-1">{errors.iconName.message}</p>
          )}
        </div>

        {/* Color Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Color
          </label>
          <div className="flex flex-wrap gap-3">
            {suggestedColors.map((color) => (
              <label
                key={color.code}
                className={`w-10 h-10 rounded-full border cursor-pointer transition-all ${
                  watchedColorCode === color.code
                    ? 'border-[3px] border-white dark:border-gray-800 ring-2 ring-gray-400 dark:ring-gray-500'
                    : 'border-2 border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                }`}
                style={{ backgroundColor: color.code }}
                title={color.name}
              >
                <input
                  type="radio"
                  className="sr-only"
                  {...register('colorCode')}
                  value={color.code}
                />
              </label>
            ))}
          </div>
          {errors.colorCode && (
            <p className="text-red-500 dark:text-red-400 text-sm mt-1">{errors.colorCode.message}</p>
          )}
        </div>

        {/* Frequency (Read-only if habit has completions) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Frequency
          </label>
          <div className={`p-4 rounded-xl border-2 ${
            hasCompletions 
              ? 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700' 
              : 'border-blue-200 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/30'
          }`}>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {getFrequencyDisplayText()}
              </span>
              {hasCompletions && (
                <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-600 px-2 py-1 rounded">
                  Cannot edit
                </span>
              )}
            </div>
            {hasCompletions && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                Frequency cannot be changed because this habit has completion history.
              </p>
            )}
          </div>
        </div>

        {/* Target Streak Length */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Target Streak Length (days)
          </label>
          <input
            {...register('targetStreakLength', { valueAsNumber: true })}
            type="number"
            min="1"
            max="365"
            placeholder="e.g., 30"
            className="input"
          />
          {errors.targetStreakLength && (
            <p className="text-red-500 dark:text-red-400 text-sm mt-1">{errors.targetStreakLength.message}</p>
          )}
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
            Your goal is to maintain a streak of {watch('targetStreakLength') || habit.targetStreakLength} days
          </p>
        </div>

        {/* Active Status */}
        <div>
          <label className="flex items-center p-4 rounded-xl border-2 border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 cursor-pointer">
            <input
              type="checkbox"
              {...register('isActive')}
              className="w-4 h-4 text-blue-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 dark:focus:ring-blue-400 focus:ring-2"
            />
            <div className="ml-3">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Active Habit
              </span>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {watchedIsActive 
                  ? 'This habit is active and will appear in your dashboard'
                  : 'This habit is paused and will not appear in your dashboard'
                }
              </p>
            </div>
          </label>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 py-3 px-4 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-xl transition-colors"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 py-3 px-4 bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 disabled:bg-blue-400 dark:disabled:bg-blue-500 text-white font-medium rounded-xl transition-colors disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Updating Habit...' : 'Update Habit'}
          </button>
        </div>
      </form>
    </div>
  );
} 